/** Site overview: address, status, publish, store summary (last 30 days). Route /sites/:id */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Col, Descriptions, Empty, Input, Modal, Row, Space, Statistic, Table, Tag, Typography, message } from 'antd';
import { CloudUploadOutlined, EditOutlined, ExportOutlined, GlobalOutlined, LockOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from '../../../shared/i18n';
import { QUIET } from './api';
import { AiJobBanner } from './ai';
import { PlansModal, StorageBar, showPlans } from './Media';
import { CopyLine, StatusTag, editorPath, fmtDate, fromMinor, money, sitePath, toMinor, siteUrl, toastErr, useLoad, useSite } from './common';

const { Text } = Typography;

const Overview = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { site, siteId, api, reload } = useSite();
    const [pubOpen, setPubOpen] = useState(false);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const { data: stats } = useLoad(() => api.get('/analytics/summary', { days: 30 }, QUIET), [api], { onError: () => {} });
    const { data: storage } = useLoad(() => api.get('/storage', null, QUIET), [api], { onError: () => {} });
    const url = siteUrl(site);
    const hasCustom = (site.domains || []).some((d) => d.kind === 'custom');

    const publish = async () => {
        setBusy(true);
        try {
            const r = await api.post('/publish', { note: note || undefined }, { timeout: 120000 });
            message.success(t('Published version {{v}}', { v: r?.version }));
            setPubOpen(false);
            setNote('');
            reload();
        } catch (e) { toastErr(e, t('Publish failed')); } finally { setBusy(false); }
    };

    const cur = stats?.currency || 'USD';
    const series = stats?.series || [];
    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <AiJobBanner siteId={siteId} onDone={reload} />
            <Card>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={14}>
                        <Descriptions column={1} size="small" colon={false} labelStyle={{ width: 110 }}>
                            <Descriptions.Item label={t('Address')}>
                                <Space wrap size={4}>
                                    <CopyLine value={url} />
                                    {site.published_version && <a href={url} target="_blank" rel="noopener noreferrer"><ExportOutlined /></a>}
                                    {!hasCustom && <Button size="small" type="link" icon={<GlobalOutlined />} onClick={() => navigate(sitePath(siteId, 'domains'))}>{t('Get a domain')}</Button>}
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Status')}><StatusTag value={site.status} />{site.settings?.access?.enabled && (
                                <Tag icon={<LockOutlined />} style={{ cursor: 'pointer' }} onClick={() => navigate(sitePath(siteId, 'settings'))}>{t('Password protected')}</Tag>)}</Descriptions.Item>
                            <Descriptions.Item label={t('Published')}>
                                {site.published_version ? `v${site.published_version} · ${fmtDate(site.published_at)}` : <Text type="secondary">{t('Not published yet')}</Text>}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Pages')}>{site.pages ?? '-'}</Descriptions.Item>
                            {site.plan && <Descriptions.Item label={t('Plan')}><Space size={4}>{site.subscription_id ? t('Paid plan') : t('Free')}<Button size="small" type="link" onClick={showPlans}>{t('Upgrade')}</Button></Space></Descriptions.Item>}
                        </Descriptions>
                    </Col>
                    <Col xs={24} md={10} style={{ textAlign: 'right' }}>
                        {storage && <div style={{ maxWidth: 280, marginLeft: 'auto', marginBottom: 12, textAlign: 'left' }}><StorageBar usage={storage} /></div>}
                        <Space wrap>
                            <Button icon={<EditOutlined />} onClick={() => navigate(editorPath(siteId))}>{t('Edit site')}</Button>
                            <Button type="primary" icon={<CloudUploadOutlined />} onClick={() => setPubOpen(true)} disabled={site.status === 'suspended'}>{t('Publish')}</Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Card title={t('Store · last 30 days')} size="small" extra={<a onClick={() => navigate(sitePath(siteId, 'analytics'))}>{t('Analytics', { context: 'shop' })}</a>}>
                {!stats ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No data yet')} /> : (
                    <>
                        <Row gutter={[16, 16]}>
                            <Col xs={12} md={6}><Statistic title={t('Revenue')} value={money(stats.revenue, cur)} /></Col>
                            <Col xs={12} md={6}><Statistic title={t('Orders')} value={stats.orders_count || 0} /></Col>
                            <Col xs={12} md={6}><Statistic title={t('Average order')} value={money(stats.average_order_value, cur)} /></Col>
                            <Col xs={12} md={6}>
                                <Statistic title={t('To fulfill')} value={stats.unfulfilled || 0}
                                    valueStyle={stats.unfulfilled ? { color: '#d46b08', cursor: 'pointer' } : undefined} />
                            </Col>
                            <Col xs={12} md={6}><Statistic title={t('New customers')} value={stats.customers_new || 0} /></Col>
                            <Col xs={12} md={6}><Statistic title={t('Abandoned checkouts')} value={stats.abandoned_checkouts || 0} /></Col>
                        </Row>
                        {series.some((x) => Number(x.revenue) > 0 || Number(x.orders) > 0) && (
                            <ReactECharts style={{ height: 220, marginTop: 16 }} notMerge option={{
                                grid: { left: 48, right: 16, top: 16, bottom: 28 },
                                tooltip: { trigger: 'axis', valueFormatter: (v) => money(toMinor(v, cur), cur) },
                                xAxis: { type: 'category', data: series.map((x) => String(x.date).slice(5)), axisTick: { show: false } },
                                yAxis: { type: 'value', splitLine: { lineStyle: { opacity: 0.3 } } },
                                series: [{ type: 'bar', name: t('Revenue'), data: series.map((x) => fromMinor(Number(x.revenue) || 0, cur)), itemStyle: { color: '#2357ff', borderRadius: [3, 3, 0, 0] } }],
                            }} />
                        )}
                        {(stats.top_products || []).length > 0 && (
                            <Table size="small" style={{ marginTop: 12 }} rowKey={(r) => r.product_id || r.title} pagination={false} dataSource={stats.top_products}
                                columns={[
                                    { title: t('Top products'), dataIndex: 'title', render: (v, r) => (r.product_id ? <a onClick={() => navigate(sitePath(siteId, `products/${r.product_id}`))}>{v}</a> : v) },
                                    { title: t('Sold'), dataIndex: 'quantity', width: 90, align: 'right' },
                                    { title: t('Revenue'), dataIndex: 'revenue', width: 130, align: 'right', render: (v) => money(v, cur) },
                                ]} />
                        )}
                    </>
                )}
            </Card>

            <PlansModal />
            <Modal open={pubOpen} title={t('Publish')} onCancel={() => setPubOpen(false)} onOk={publish} confirmLoading={busy} okText={t('Publish')}>
                <Input placeholder={t('Note (optional)')} value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} onPressEnter={publish} />
            </Modal>
        </Space>
    );
};

export default Overview;
